import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, DemoBadge } from "@/components/ui-kit";
import { Loader } from "@/components/Loader";
import { JourneyCard } from "@/components/JourneyCard";
import { UserPlus, Users, HeartHandshake, ClipboardCheck, Check, X, ShieldAlert, MapPin, ChevronRight, Siren } from "lucide-react";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/users/me/community"), api.get("/readiness"), api.get("/emergencies/active")])
      .then(([c, r, a]) => setData({ community: c.data, readiness: r.data, active: a.data?.id ? a.data : null }))
      .catch(() => setData({ community: {}, readiness: {}, active: null }));
  }, []);

  if (!data) return <Loader />;
  const community = data.community?.community;
  const r = data.readiness;
  const firstName = (user?.full_name || "there").split(" ")[0];

  const quick = [
    { i: UserPlus, t: "Add Trusted Contact", to: "/profile/trusted-contacts", id: "qa-contact" },
    { i: Users, t: "View Community", to: "/community", id: "qa-community" },
    { i: HeartHandshake, t: "Care Circle", to: "/care", id: "qa-care" },
    { i: ClipboardCheck, t: "Test Readiness", to: "/safety", id: "qa-readiness" },
  ];

  const checklist = [
    { t: "Profile complete", ok: r.profile_complete },
    { t: "Home community confirmed", ok: r.home_community_confirmed },
    { t: "Trusted contacts added", ok: r.trusted_contacts_added },
    { t: "Emergency permissions tested", ok: r.permissions_tested },
  ];

  return (
    <div className="ll-fade-up" data-testid="home-screen">
      <header className="px-5 pt-8 pb-2 flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#C4B5FD]/30 overflow-hidden flex items-center justify-center shrink-0">
          {user?.profile_photo_url ? <img src={fileUrl(user.profile_photo_url)} alt="" className="w-full h-full object-cover" /> : <span className="font-bold text-[#111827]">{firstName[0]}</span>}
        </div>
        <div>
          <h1 className="text-2xl font-bold font-head tracking-tight" data-testid="home-greeting">{greeting()}, {firstName}</h1>
          <p className="text-[#111827]/60 text-sm flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {community ? `${community.name}, ${community.city}` : "No community selected"}</p>
        </div>
      </header>

      <div className="px-5 mt-4">
        {data.active && (
          <button data-testid="resume-emergency" onClick={() => navigate(`/emergency/${data.active.id}`)} className="w-full mb-4 bg-[#DC2626] text-white rounded-2xl p-4 flex items-center gap-3 shadow-lg animate-pulse">
            <Siren className="w-6 h-6 shrink-0" />
            <div className="text-left flex-1"><p className="font-bold">Active emergency in progress</p><p className="text-sm text-white/80">{data.active.type_label} · tap to resume coordination</p></div>
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl overflow-hidden relative shadow-lg">
          <img src="https://images.unsplash.com/photo-1512676052261-98bab4919138?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBuZWlnaGJvcmhvb2QlMjBjb21tdW5pdHl8ZW58MHx8fHwxNzgzNjQzNzc3fDA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-40 object-cover" />
          <div className="absolute inset-0 bg-[#111827]/55" />
          <div className="absolute inset-0 p-6 flex flex-col justify-end">
            <h2 className="text-white text-2xl font-bold font-head leading-tight">Your neighbourhood, connected when it matters.</h2>
          </div>
        </motion.div>

        <button data-testid="get-help-button" onClick={() => navigate("/emergency/sos")} className="w-full mt-4 bg-[#DC2626] text-white rounded-3xl py-6 text-2xl font-black shadow-lg flex items-center justify-center gap-3 hover:shadow-xl active:scale-[0.98] transition-transform">
          <Siren className="w-8 h-8" /> Get Help
        </button>
      </div>

      <div className="px-5 mt-4">
        <JourneyCard />
      </div>

      <div className="px-5 mt-6 grid grid-cols-2 gap-3">
        {quick.map((q) => (
          <button key={q.id} data-testid={q.id} onClick={() => navigate(q.to)} className="bg-white rounded-2xl p-4 text-left shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5 hover:-translate-y-0.5 transition-transform min-h-[92px] flex flex-col justify-between">
            <div className="w-10 h-10 rounded-xl bg-[#F97316]/10 flex items-center justify-center"><q.i className="w-5 h-5 text-[#F97316]" /></div>
            <span className="font-bold text-sm mt-2">{q.t}</span>
          </button>
        ))}
      </div>

      <div className="px-5 mt-6">
        <Card data-testid="readiness-card">
          <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold font-head">Your readiness</h3><ClipboardCheck className="w-5 h-5 text-[#111827]/40" /></div>
          <div className="space-y-3">
            {checklist.map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${c.ok ? "bg-[#16A34A]" : "bg-[#111827]/10"}`}>{c.ok ? <Check className="w-4 h-4 text-white" /> : <X className="w-3.5 h-3.5 text-[#111827]/40" />}</div>
                <span className={`text-base ${c.ok ? "text-[#111827]" : "text-[#111827]/50"}`}>{c.t}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="px-5 mt-4">
        <Card data-testid="community-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold font-head flex items-center gap-2"><Users className="w-5 h-5 text-[#F97316]" /> Community</h3>
            <DemoBadge />
          </div>
          {community ? (
            <>
              <p className="text-xl font-bold">{community.name}</p>
              <p className="text-[#111827]/60 text-sm mb-4">{community.city}, {community.state} · <span className="capitalize font-semibold text-[#F97316]">{community.status}</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#FFF7E6] rounded-2xl p-4 text-center"><p className="text-2xl font-black text-[#111827]">{community.demo_residents ?? 0}</p><p className="text-xs text-[#111827]/60 font-semibold">Residents</p></div>
                <div className="bg-[#FFF7E6] rounded-2xl p-4 text-center"><p className="text-2xl font-black text-[#111827]">{community.demo_responders ?? 0}</p><p className="text-xs text-[#111827]/60 font-semibold">Responders</p></div>
              </div>
              <button data-testid="view-community-detail" onClick={() => navigate("/community")} className="w-full mt-4 flex items-center justify-center gap-1 text-[#F97316] font-bold py-2">View details <ChevronRight className="w-4 h-4" /></button>
            </>
          ) : (
            <div className="text-center py-4">
              <ShieldAlert className="w-8 h-8 text-[#111827]/30 mx-auto mb-2" />
              <p className="text-[#111827]/60 mb-3">No home community selected yet.</p>
              <button onClick={() => navigate("/profile/community")} data-testid="select-community" className="bg-[#F97316] text-white rounded-2xl py-3 px-6 font-bold">Select community</button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
